import { locationsAPI } from '../api';
import { geocodeLocation } from './geolocation';

const ZIP_KEY = 'userZipCode';
const LOCATION_KEY = 'userDeliveryLocation';

export interface StoredDeliveryLocation {
  zip?: string;
  city?: string;
  state?: string;
  lat?: number;
  lng?: number;
  label?: string;
  city_id?: number;
  state_id?: number;
}

export function extractZipFromAddress(address: string): string | null {
  const match = address.match(/\b(\d{5})(?:-\d{4})?\b/);
  return match ? match[1] : null;
}

export function loadHomeDeliveryLocation(): StoredDeliveryLocation | null {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as StoredDeliveryLocation;
      if (
        parsed &&
        (parsed.zip ||
          parsed.city ||
          parsed.label ||
          parsed.city_id != null ||
          parsed.state_id != null ||
          (parsed.lat != null && parsed.lng != null))
      ) {
        return parsed;
      }
    }
  } catch {
    /* ignore */
  }
  const zip = localStorage.getItem(ZIP_KEY);
  return zip ? { zip } : null;
}

export function saveHomeDeliveryLocation(update: {
  zip?: string | null;
  city?: string | null;
  state?: string | null;
  lat?: number | null;
  lng?: number | null;
  label?: string | null;
  fullAddress?: string | null;
  city_id?: number | null;
  state_id?: number | null;
}) {
  const existing = loadHomeDeliveryLocation() || {};
  const zipFromAddress = update.fullAddress ? extractZipFromAddress(update.fullAddress) : null;
  const zip = (update.zip || zipFromAddress || existing.zip || '').replace(/\D/g, '').slice(0, 5);

  const next: StoredDeliveryLocation = { ...existing };
  if (zip) next.zip = zip;

  if (update.city != null) {
    const city = String(update.city).trim();
    if (city) next.city = city;
  } else if (existing.city) {
    next.city = existing.city;
  }

  if (update.state != null) {
    const state = String(update.state).trim();
    if (state) next.state = state;
  } else if (existing.state) {
    next.state = existing.state;
  }

  if (update.city_id != null && update.city_id > 0) next.city_id = update.city_id;
  if (update.state_id != null && update.state_id > 0) next.state_id = update.state_id;

  if (update.lat != null && update.lng != null) {
    next.lat = update.lat;
    next.lng = update.lng;
  }

  if (update.label?.trim()) next.label = update.label.trim();
  else if (update.fullAddress?.trim()) next.label = update.fullAddress.trim();

  if (!next.zip && next.lat == null && !next.city && !next.label && next.city_id == null && next.state_id == null) {
    return;
  }

  try {
    if (next.zip) localStorage.setItem(ZIP_KEY, next.zip);
    localStorage.setItem(LOCATION_KEY, JSON.stringify(next));
  } catch {
    /* private mode / quota — still notify listeners with in-memory expectation */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('delivery-location-updated'));
  }
}

export function formatStoredDeliveryLabel(stored: StoredDeliveryLocation | null | undefined): string {
  if (!stored) return '';
  const label = (stored.label || '').trim();
  if (label) {
    const street = label.split(',')[0]?.trim();
    return street || label;
  }
  if (stored.city && stored.state) return `${stored.city}, ${stored.state}`;
  if (stored.zip) return stored.zip;
  return '';
}

/** True when buyer saved a delivery address. */
export function hasUserDeliveryLocation(): boolean {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as StoredDeliveryLocation;
    if (!parsed || typeof parsed !== 'object') return false;
    if ((parsed.label || '').trim()) return true;
    if ((parsed.city || '').trim() && (parsed.state || '').trim()) return true;
    if (parsed.lat != null && parsed.lng != null) return true;
    if (parsed.city_id != null || parsed.state_id != null) return true;
    if ((parsed.zip || '').trim()) return true;
    return false;
  } catch {
    return false;
  }
}

export function deliveryAreaLabel(stored: StoredDeliveryLocation | null | undefined): string | null {
  if (!stored) return null;
  const label = formatStoredDeliveryLabel(stored);
  return label || null;
}

/**
 * Persist address immediately, then geocode in the background.
 * Waiting on geocode before save caused addresses to vanish when the request was slow.
 */
export async function saveDeliveryLocationFromModal(
  cityName: string,
  stateName: string,
  fullAddress?: string,
  cityId?: number,
  stateId?: number,
): Promise<StoredDeliveryLocation | null> {
  const streetLine = fullAddress ? fullAddress.split(',')[0]?.trim() || '' : '';
  const label = streetLine || fullAddress || [cityName, stateName].filter(Boolean).join(', ');
  const payload = {
    fullAddress,
    city: cityName,
    state: stateName,
    label,
    city_id: cityId ?? null,
    state_id: stateId ?? null,
  };

  // Save first so a refresh still has the address even if geocode is slow/fails.
  saveHomeDeliveryLocation(payload);

  try {
    const coords = fullAddress
      ? await locationsAPI
          .geocode(fullAddress)
          .then((r: { lat: number | null; lon?: number | null; lng?: number | null }) => {
            const lng = r?.lon ?? r?.lng;
            return r?.lat != null && lng != null ? { lat: r.lat, lng } : null;
          })
      : await geocodeLocation(cityName, stateName);
    if (coords) {
      saveHomeDeliveryLocation({ ...payload, lat: coords.lat, lng: coords.lng });
    }
  } catch {
    /* address already saved without coords */
  }
  return loadHomeDeliveryLocation();
}
